import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, ArrowRight, Book, Eye } from 'lucide-react';
import { booksData } from '../newMock';

// Analytics tracking helper
const trackEvent = async (event, data = {}) => {
  try {
    await fetch(`/api/analytics/${event}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  } catch (e) {
    console.log('Analytics tracking failed:', e);
  }
};

const BookShowcaseSection = ({ onPurchaseBook, onPurchaseBundle }) => {
  const [loadedImages, setLoadedImages] = useState({});

  const handleImageLoad = (bookId) => {
    setLoadedImages(prev => ({ ...prev, [bookId]: true }));
  };

  const handlePreview = (bookId) => {
    // Track preview click
    trackEvent('preview-clicked', { book_id: bookId });
    // Open preview PDF in new tab
    window.open(`/api/preview/${bookId}`, '_blank');
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Bundle Pricing Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-green-100 rounded-full px-4 py-2 mb-4">
            <Book className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800 font-semibold">Complete Series Available</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {booksData.bundle.title}
          </h2>
          
          <div className="flex items-center justify-center space-x-4 mb-6">
            <span className="text-2xl text-gray-500 line-through">₹{booksData.bundle.originalPrice.toLocaleString()}</span>
            <span className="text-5xl font-bold text-green-600">₹{booksData.bundle.price.toLocaleString()}</span>
            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              {booksData.bundle.discount}
            </span>
          </div>
          
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {booksData.bundle.description}
          </p>
          
          <Button
            onClick={onPurchaseBundle}
            size="lg"
            className="bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white font-bold py-4 px-8 text-xl rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
          >
            Get All 3 Books - ₹{booksData.bundle.price.toLocaleString()}
          </Button>
        </div>

        {/* Individual Books */}
        <div className="space-y-20">
          {Object.values(booksData.individual).map((book, index) => (
            <div key={book.id} className={`flex flex-col lg:flex-row items-center gap-12 ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              
              {/* Book Cover */}
              <div className="lg:w-1/3 flex flex-col items-center">
                <div className="relative group">
                  {!loadedImages[book.id] && (
                    <div className="w-64 h-80 bg-gray-200 animate-pulse rounded-lg flex items-center justify-center">
                      <Book className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <img
                    src={book.coverImage}
                    alt={`${book.title} Cover`}
                    className={`w-64 h-80 object-cover rounded-lg shadow-xl transition-transform duration-300 group-hover:scale-105 ${
                      loadedImages[book.id] ? 'opacity-100' : 'opacity-0 absolute'
                    }`}
                    loading="lazy"
                    onLoad={() => handleImageLoad(book.id)}
                    style={{ maxWidth: '120kb' }} // Hint for optimization
                  />
                </div>
                {/* Preview Button */}
                <Button
                  onClick={() => handlePreview(book.id)}
                  variant="outline"
                  className="mt-4 text-blue-600 border-blue-600 hover:bg-blue-50 font-medium"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Read Free Preview
                </Button>
              </div>

              {/* Book Content */}
              <div className="lg:w-2/3 space-y-6">
                <div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {book.title}
                  </h3>
                  <p className="text-xl text-gray-600 mb-4">
                    {book.subtitle}
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {book.description}
                  </p>
                </div>

                {/* Key Areas */}
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Key areas covered:</h4>
                  <div className="space-y-3">
                    {book.keyAreas.map((area, areaIndex) => (
                      <div key={areaIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{area}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing and CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-gray-50 rounded-xl p-6 space-y-4 sm:space-y-0">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">₹{book.price.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Individual purchase</div>
                  </div>
                  <Button
                    onClick={() => onPurchaseBook(book.id)}
                    className="bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300"
                  >
                    Buy {book.title}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Final Bundle CTA */}
        <div className="text-center mt-20 bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Get the Complete Series & Save Big
          </h3>
          <p className="text-xl text-gray-600 mb-8">
            All three books together provide a comprehensive guide for every stage of marriage.
          </p>
          <div className="flex items-center justify-center space-x-4 mb-6">
            <span className="text-xl text-gray-500">Individual total: ₹4,000</span>
            <ArrowRight className="w-6 h-6 text-gray-400" />
            <span className="text-3xl font-bold text-green-600">Bundle: ₹{booksData.bundle.price.toLocaleString()}</span>
          </div>
          <Button
            onClick={onPurchaseBundle}
            size="lg"
            className="bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white font-bold py-4 px-8 text-xl rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
          >
            Get Complete Series - Save ₹1,500
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BookShowcaseSection;